import type { ECharts } from 'echarts';
import { ParamsConfig } from './validator-factory';

export interface DragCallbacks {
  onDragStart?: (seriesIndex: number, dataIndex: number) => void;
  onDrag?: (seriesIndex: number, dataIndex: number, newValue: number) => void;
  onDrop?: (seriesIndex: number, dataIndex: number, newValue: number) => void;
  onZoomChanged?: () => void;
}

interface DragState {
  dragging: boolean;
  seriesIndex: number;
  dataIndex: number;
  startY: number;
  originalValue: number;
  yAxisIndex: number;
}

interface ZoomSelectState {
  selecting: boolean;
  startX: number;
}

export class EChartsDragHandler {
  private chart: ECharts;
  private callbacks: DragCallbacks;
  private dragState: DragState = { dragging: false, seriesIndex: -1, dataIndex: -1, startY: 0, originalValue: 0, yAxisIndex: 0 };
  private zoomState: ZoomSelectState = { selecting: false, startX: 0 };
  private lastExtremesSet = 0;
  private startMax = 0;
  private readonly HIT_RADIUS = 12;
  private readonly PRECISION = 0.5;
  private readonly MIN_ZOOM_PIXELS = 20;

  public drillDownEnabled = false;

  private boundMouseDown: (e: any) => void;
  private boundMouseMove: (e: any) => void;
  private boundMouseUp: (e: any) => void;

  constructor(chart: ECharts, callbacks: DragCallbacks) {
    this.chart = chart;
    this.callbacks = callbacks;

    this.boundMouseDown = this.onMouseDown.bind(this);
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);

    const zr = (this.chart as any).getZr();
    zr.on('mousedown', this.boundMouseDown);
    zr.on('mousemove', this.boundMouseMove);
    zr.on('mouseup', this.boundMouseUp);
  }

  get isDragging(): boolean {
    return this.dragState.dragging;
  }

  destroy(): void {
    const zr = (this.chart as any)?.getZr?.();
    if (!zr) return;
    zr.off('mousedown', this.boundMouseDown);
    zr.off('mousemove', this.boundMouseMove);
    zr.off('mouseup', this.boundMouseUp);
  }

  private getGridRect(): { x: number; y: number; width: number; height: number } | null {
    try {
      const gridModel = (this.chart as any).getModel().getComponent('grid', 0);
      return gridModel?.coordinateSystem?.getRect?.();
    } catch {
      return null;
    }
  }

  private isInsideGrid(mouseX: number, mouseY: number): boolean {
    const rect = this.getGridRect();
    if (!rect) return false;
    return mouseX >= rect.x && mouseX <= rect.x + rect.width &&
           mouseY >= rect.y && mouseY <= rect.y + rect.height;
  }

  private isOverXAxisLabels(mouseX: number, mouseY: number): boolean {
    if (!this.drillDownEnabled) return false;
    const rect = this.getGridRect();
    if (!rect) return false;
    const labelTop = rect.y + rect.height;
    const labelBottom = labelTop + 30;
    return mouseX >= rect.x && mouseX <= rect.x + rect.width &&
           mouseY >= labelTop && mouseY <= labelBottom;
  }

  private findHitPoint(mouseX: number, mouseY: number): { seriesIndex: number; dataIndex: number } | null {
    const option = this.chart.getOption() as any;
    const series = option.series || [];

    for (let si = 0; si < series.length; si++) {
      const s = series[si];
      if (!s || !s._isDraggable) continue;

      const data = s.data || [];
      for (let di = 0; di < data.length; di++) {
        const value = typeof data[di] === 'object' ? data[di].value : data[di];
        if (value == null || isNaN(value)) continue;

        const point = this.chart.convertToPixel(
          { seriesIndex: si },
          [di, value]
        );
        if (!point) continue;

        const dx = mouseX - point[0];
        const dy = mouseY - point[1];
        if (Math.sqrt(dx * dx + dy * dy) < this.HIT_RADIUS) {
          return { seriesIndex: si, dataIndex: di };
        }
      }
    }
    return null;
  }

  // --- Zoom select ---

  private startZoomSelect(e: any): void {
    if (!this.isInsideGrid(e.offsetX, e.offsetY)) return;

    this.zoomState = { selecting: true, startX: e.offsetX };
    const gridRect = this.getGridRect();
    if (!gridRect) return;

    // Create initial highlight rect (zero width)
    this.chart.setOption({
      graphic: [{
        id: 'zoomHighlight',
        type: 'rect',
        z: 100,
        silent: true,
        shape: { x: e.offsetX, y: gridRect.y, width: 0, height: gridRect.height },
        style: { fill: 'rgba(51,133,255,0.15)' }
      }]
    } as any);
  }

  private updateZoomSelect(e: any): void {
    const gridRect = this.getGridRect();
    if (!gridRect) return;

    const startX = this.zoomState.startX;
    const currentX = Math.max(gridRect.x, Math.min(e.offsetX, gridRect.x + gridRect.width));
    const left = Math.min(startX, currentX);
    const width = Math.abs(currentX - startX);

    this.chart.setOption({
      graphic: [{
        id: 'zoomHighlight',
        shape: { x: left, y: gridRect.y, width, height: gridRect.height }
      }]
    } as any);
  }

  private endZoomSelect(e: any): void {
    const startX = this.zoomState.startX;
    const endX = e.offsetX;
    this.zoomState = { selecting: false, startX: 0 };

    // Remove highlight rect
    this.chart.setOption({
      graphic: [{ id: 'zoomHighlight', $action: 'remove' }]
    } as any);

    // Only zoom if dragged far enough
    if (Math.abs(endX - startX) < this.MIN_ZOOM_PIXELS) return;

    const gridRect = this.getGridRect();
    if (!gridRect) return;

    // Clamp to grid bounds
    const left = Math.max(gridRect.x, Math.min(startX, endX));
    const right = Math.min(gridRect.x + gridRect.width, Math.max(startX, endX));

    // Convert pixel range to percentage of the full data range
    // Account for current dataZoom state
    const option = this.chart.getOption() as any;
    const dataZoom = option.dataZoom;
    const currentStart = dataZoom?.[0]?.start ?? 0;
    const currentEnd = dataZoom?.[0]?.end ?? 100;
    const currentRange = currentEnd - currentStart;

    const leftPercent = (left - gridRect.x) / gridRect.width;
    const rightPercent = (right - gridRect.x) / gridRect.width;

    const newStart = currentStart + leftPercent * currentRange;
    const newEnd = currentStart + rightPercent * currentRange;

    this.chart.dispatchAction({
      type: 'dataZoom',
      dataZoomIndex: 0,
      start: newStart,
      end: newEnd
    });

    this.callbacks.onZoomChanged?.();
  }

  // --- Point drag ---

  private onMouseDown(e: any): void {
    const hit = this.findHitPoint(e.offsetX, e.offsetY);
    if (!hit) {
      this.startZoomSelect(e);
      return;
    }

    const option = this.chart.getOption() as any;
    const series = option.series[hit.seriesIndex];
    if (!series._isDraggable) {
      this.startZoomSelect(e);
      return;
    }

    const dataItem = series.data[hit.dataIndex];
    const value = typeof dataItem === 'object' ? dataItem.value : dataItem;

    const yAxisIndex = series.yAxisIndex || 0;

    this.dragState = {
      dragging: true,
      seriesIndex: hit.seriesIndex,
      dataIndex: hit.dataIndex,
      startY: e.offsetY,
      originalValue: value,
      yAxisIndex
    };

    // Freeze Y-axis range at start to prevent jumps
    const yAxis = option.yAxis[yAxisIndex];
    let min = yAxis.min;
    let max = yAxis.max;
    if (min == null || max == null) {
      const axisModel = (this.chart as any).getModel().getComponent('yAxis', yAxisIndex);
      const extent = axisModel?.axis?.scale?.getExtent?.();
      if (extent) {
        min = extent[0];
        max = extent[1];
      }
    }
    if (max != null && min != null && max - min < 5) {
      max += 3;
      min -= 3;
    }
    this.startMax = max || 0;

    this.callbacks.onDragStart?.(hit.seriesIndex, hit.dataIndex);
  }

  private onMouseMove(e: any): void {
    if (this.zoomState.selecting) {
      this.updateZoomSelect(e);
      return;
    }

    if (!this.dragState.dragging) {
      const hit = this.findHitPoint(e.offsetX, e.offsetY);
      const zr = (this.chart as any).getZr();
      const cursor = hit ? 'pointer' : this.isOverXAxisLabels(e.offsetX, e.offsetY) ? 'pointer' : 'default';
      zr.setCursorStyle(cursor);
      return;
    }

    const { seriesIndex, dataIndex, yAxisIndex } = this.dragState;
    const option = this.chart.getOption() as any;
    const series = option.series[seriesIndex];

    const dataPoint = this.chart.convertFromPixel(
      { seriesIndex },
      [e.offsetX, e.offsetY]
    );
    if (!dataPoint) return;

    let newValue = dataPoint[1];

    newValue = Math.round(newValue / this.PRECISION) * this.PRECISION;

    const dataItem = series.data[dataIndex];
    const metadata = typeof dataItem === 'object' ? dataItem._metadata : null;
    const rowMetadata = series._metadata?.row?.metadata;

    let minVal: number | null = null;
    let maxVal: number | null = null;

    if (metadata?.maxValue || metadata?.minValue) {
      maxVal = metadata.maxValue || null;
      minVal = metadata.minValue || null;
    } else if (rowMetadata) {
      let paramName = rowMetadata.paramName;
      const isWorkload = metadata?.workload;
      if (isWorkload && (ParamsConfig as any)[paramName + '_WORKLOAD']) {
        paramName += '_WORKLOAD';
      }
      if ((ParamsConfig as any)[paramName]) {
        minVal = (ParamsConfig as any)[paramName].min;
        maxVal = (ParamsConfig as any)[paramName].max;
      }
    }

    if (minVal != null && newValue < minVal) newValue = minVal;
    if (maxVal != null && newValue > maxVal) newValue = maxVal;

    const validator = series._metadata?.row?.metadata?.validator;
    const pointMetadata = metadata;
    if (validator?.isValid && !validator.isValid(newValue, pointMetadata)) {
      return;
    }

    const newData = [...series.data];
    if (typeof newData[dataIndex] === 'object') {
      newData[dataIndex] = { ...newData[dataIndex], value: newValue };
    } else {
      newData[dataIndex] = newValue;
    }

    const seriesUpdate = option.series.map((s: any, i: number) => {
      if (i === seriesIndex) {
        return { ...s, data: newData };
      }
      return s;
    });

    this.chart.setOption({ series: seriesUpdate }, { replaceMerge: [] });

    const yAxis = option.yAxis[yAxisIndex];
    const currentMax = yAxis.max || this.startMax;
    if (newValue * 1.05 > currentMax) {
      const now = Date.now();
      if (now - this.lastExtremesSet > 1000) {
        this.lastExtremesSet = now;
      }
    }

    this.chart.dispatchAction({
      type: 'showTip',
      seriesIndex,
      dataIndex
    });

    this.callbacks.onDrag?.(seriesIndex, dataIndex, newValue);
  }

  private onMouseUp(e: any): void {
    if (this.zoomState.selecting) {
      this.endZoomSelect(e);
      return;
    }

    if (!this.dragState.dragging) return;

    const { seriesIndex, dataIndex } = this.dragState;
    const option = this.chart.getOption() as any;
    const series = option.series[seriesIndex];
    const dataItem = series.data[dataIndex];
    const finalValue = typeof dataItem === 'object' ? dataItem.value : dataItem;

    this.dragState = { dragging: false, seriesIndex: -1, dataIndex: -1, startY: 0, originalValue: 0, yAxisIndex: 0 };

    this.callbacks.onDrop?.(seriesIndex, dataIndex, finalValue);
  }
}
